<template>
    <div>
        <el-upload
            class="oss-uploader"
            :class="{ 'oss-uploader--hidden': hidden }"
            ref="upload"
            :action="uploadUrl"
            :accept="accept"
            :before-upload="beforeUpload"
            :disabled="disabled"
            :file-list="fileList"
            :http-request="handleUpload"
            :limit="max"
            :list-type="listType"
            :multiple="multiple"
            :on-change="handleChange"
            :on-error="handleError"
            :on-exceed="handleExceed"
            :on-preview="handlePreview"
            :on-remove="handleRemove"
            :on-success="handleSuccess"
            :show-file-list="uploadShowFileList">
            <slot v-if="!hidden">
                <i class="el-icon-plus"></i>
            </slot>
            <div v-if="!hidden && uploadTip" slot="tip" class="el-upload__tip">{{ uploadTip }}</div>
        </el-upload>

        <image-viewer
            v-if="showImagePreview"
            :initial-index="previewIndex"
            :on-close="closeImagePreview"
            :url-list="previewList" />
    </div>
</template>

<script>
import OSS from 'ali-oss'
import ImageViewer from './image-viewer'

const STS_CACHE_BUFFER_MS = 5 * 60 * 1000

let cachedStsConfig = null
let cachedStsPromise = null

export default {
    name: 'OssUpload',
    components: {
        ImageViewer
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        accept: {
            type: String,
            default: 'image/*'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        folder: {
            type: String,
            default: ''
        },
        hidden: {
            type: Boolean,
            default: false
        },
        listType: {
            type: String,
            default: 'picture-card'
        },
        max: {
            type: Number,
            default: 5
        },
        maxSize: {
            type: Number,
            default: 10
        },
        multiple: {
            type: Boolean,
            default: false
        },
        showPreview: {
            type: Boolean,
            default: true
        },
        showProgress: {
            type: Boolean,
            default: true
        },
        tip: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            fileList: this.normalizeFileList(this.value),
            isSyncingFromValue: false,
            previewIndex: 0,
            previewList: [],
            previewUrl: '',
            progress: 0,
            showImagePreview: false,
            uploading: false
        }
    },
    computed: {
        uploadShowFileList() {
            return !this.hidden
        },
        uploadTip() {
            if (this.tip) {
                return this.tip
            }
            if (this.accept.indexOf('image') !== -1) {
                return `只能上传符合要求的图片，且不超过${this.maxSize}MB`
            }
            if (this.accept.indexOf('video') !== -1) {
                return `只能上传符合要求的视频，且不超过${this.maxSize}MB`
            }
            return ''
        },
        uploadUrl() {
            return '#'
        }
    },
    watch: {
        value(val) {
            this.isSyncingFromValue = true
            this.fileList = this.normalizeFileList(val)
        },
        fileList: {
            deep: true,
            handler(newVal) {
                if (this.isSyncingFromValue) {
                    this.isSyncingFromValue = false
                    return
                }
                const normalizedList = this.normalizeFileList(newVal)
                this.$emit('input', normalizedList)
                this.$emit('change', normalizedList)
            }
        }
    },
    methods: {
        clearFiles() {
            if (this.$refs.upload) {
                this.$refs.upload.clearFiles()
            }
            if (this.hidden) {
                this.fileList = []
            }
        },
        extractUrl(file) {
            if (!file) {
                return ''
            }
            if (file.response && file.response.url) {
                return file.response.url
            }
            if (file.url) {
                return file.url
            }
            return ''
        },
        getMediaType(file) {
            const fileType = (file && file.raw && file.raw.type) || file.type || ''
            return fileType.split('/')[0] || 'image'
        },
        getObjectKey(fileName) {
            let folder = `admin/${this.folder || ''}`
            folder = folder.replace(/\/+/g, '/')
            if (folder && !folder.endsWith('/')) {
                folder += '/'
            }
            const safeFileName = (fileName || 'file').replace(/[^\w.\-()]/g, '_')
            return `${folder}${Date.now()}_${safeFileName}`
        },
        handleChange(file, fileList) {
            this.$emit('file-change', { file, fileList })
        },
        handleError(error, file, fileList) {
            this.uploading = false
            this.progress = 0
            this.$message.error('上传失败')
            this.$emit('error', { error, file, fileList })
            this.$emit('upload-end', { success: false, error, file, fileList })
        },
        handleExceed() {
            this.$message.warning(`最多只能上传${this.max}个文件`)
            this.$emit('exceed')
        },
        handlePreview(file) {
            if (!this.showPreview || this.accept.indexOf('image') === -1) {
                return
            }
            this.previewList = this.fileList.map(item => item.url).filter(Boolean)
            this.previewIndex = this.fileList.findIndex(item => item.url === file.url)
            this.previewUrl = file.url
            this.showImagePreview = true
        },
        closeImagePreview() {
            this.showImagePreview = false
        },
        handleRemove(file, fileList) {
            this.fileList = this.normalizeFileList(fileList)
            this.$emit('remove', { file, fileList: this.fileList })
        },
        handleSuccess(response, file, fileList) {
            const url = response && response.url ? response.url : this.extractUrl(file)
            if (response) {
                file.response = response
            }
            if (url) {
                file.url = url
            }
            const normalizedList = this.normalizeFileList(
                (fileList || []).map(item => {
                    if (item.uid === file.uid) {
                        return {
                            ...item,
                            response,
                            url
                        }
                    }
                    return item
                })
            )
            this.fileList = normalizedList
            this.$emit('success', {
                file,
                fileList: normalizedList,
                mediaType: this.getMediaType(file),
                response,
                url
            })
            this.$emit('upload-end', {
                success: true,
                file,
                fileList: normalizedList,
                mediaType: this.getMediaType(file),
                response,
                url
            })
            if (this.hidden) {
                this.clearFiles()
            }
        },
        async handleUpload({ file, onError, onProgress, onSuccess }) {
            this.uploading = true
            this.progress = 0
            this.$emit('upload-start', { file, mediaType: this.getMediaType({ raw: file }) })
            const objectKey = this.getObjectKey(file.name)
            try {
                const response = await this.uploadWithRetry(file, objectKey, onProgress)
                this.uploading = false
                this.progress = 0
                const url = this.buildObjectUrl(response.publicHost, objectKey)
                file.url = url
                if (onSuccess) {
                    onSuccess({ url }, file)
                }
            } catch (error) {
                this.uploading = false
                this.progress = 0
                if (onError) {
                    onError(error)
                }
            }
        },
        async uploadWithRetry(file, objectKey, onProgress) {
            try {
                return await this.multipartUpload(file, objectKey, onProgress, false)
            } catch (error) {
                if (this.shouldRefreshSts(error)) {
                    return this.multipartUpload(file, objectKey, onProgress, true)
                }
                throw error
            }
        },
        async multipartUpload(file, objectKey, onProgress, forceRefresh) {
            const stsConfig = await this.getStsConfig(forceRefresh)
            const client = this.createOssClient(stsConfig)
            await client.multipartUpload(objectKey, file, {
                progress: progressValue => {
                    this.progress = Math.floor(progressValue * 100)
                    this.$emit('upload-progress', {
                        percent: this.progress,
                        file
                    })
                    if (onProgress) {
                        onProgress({ percent: this.progress })
                    }
                }
            })
            return stsConfig
        },
        createOssClient(stsConfig) {
            return new OSS({
                region: stsConfig.region,
                accessKeyId: stsConfig.accessKeyId,
                accessKeySecret: stsConfig.accessKeySecret,
                stsToken: stsConfig.stsToken,
                bucket: stsConfig.bucket,
                secure: true
            })
        },
        async getStsConfig(forceRefresh = false) {
            if (!forceRefresh && this.isStsConfigUsable(cachedStsConfig)) {
                return cachedStsConfig
            }
            if (!forceRefresh && cachedStsPromise) {
                return cachedStsPromise
            }

            cachedStsPromise = this.$api.post(
                '/Ali_oss/sts_token',
                {},
                { emulateJSON: true }
            ).then(res => {
                const data = res.body || {}
                if (!data.accessKeyId || !data.accessKeySecret || !data.stsToken || !data.bucket || !data.region) {
                    throw new Error(data.msg || '获取 OSS 临时凭证失败')
                }
                cachedStsConfig = {
                    accessKeyId: data.accessKeyId,
                    accessKeySecret: data.accessKeySecret,
                    bucket: data.bucket,
                    expiration: data.expiration,
                    publicHost: data.publicHost,
                    region: data.region,
                    stsToken: data.stsToken
                }
                cachedStsPromise = null
                return cachedStsConfig
            }, error => {
                cachedStsPromise = null
                cachedStsConfig = null
                throw error
            })

            return cachedStsPromise
        },
        isStsConfigUsable(stsConfig) {
            if (!stsConfig || !stsConfig.expiration) {
                return false
            }
            const expireAt = new Date(stsConfig.expiration).getTime()
            if (!expireAt) {
                return false
            }
            return expireAt - Date.now() > STS_CACHE_BUFFER_MS
        },
        shouldRefreshSts(error) {
            const errorText = ((error && error.code) || '') + ' ' + ((error && error.message) || '')
            return /SecurityTokenExpired|InvalidAccessKeyId|AccessDenied|SignatureDoesNotMatch|ExpiredToken/i.test(errorText)
        },
        buildObjectUrl(publicHost, objectKey) {
            const host = (publicHost || '').replace(/\/$/, '')
            if (!host) {
                throw new Error('OSS 公网访问地址未配置')
            }
            return `${host}/${objectKey}`
        },
        isAcceptedFile(file) {
            if (!this.accept) {
                return true
            }
            const acceptedTypes = this.accept.split(',').map(item => item.trim()).filter(Boolean)
            if (!acceptedTypes.length) {
                return true
            }
            return acceptedTypes.some(type => {
                if (type.endsWith('/*')) {
                    return file.type.indexOf(type.replace('/*', '/')) === 0
                }
                if (type.startsWith('.')) {
                    return file.name.toLowerCase().endsWith(type.toLowerCase())
                }
                return file.type === type
            })
        },
        normalizeFileList(list) {
            if (!Array.isArray(list)) {
                return []
            }
            return list.map(item => {
                if (typeof item === 'string') {
                    return {
                        name: item.split('/').pop(),
                        url: item
                    }
                }
                const clonedItem = { ...item }
                if (clonedItem.response && clonedItem.response.url) {
                    clonedItem.url = clonedItem.response.url
                }
                if (!clonedItem.name && clonedItem.url) {
                    clonedItem.name = clonedItem.url.split('/').pop()
                }
                return clonedItem
            })
        },
        openPicker() {
            const uploadInput = this.$refs.upload && this.$refs.upload.$el.querySelector('input')
            if (uploadInput) {
                uploadInput.value = null
                uploadInput.click()
            }
        },
        beforeUpload(file) {
            const isAccepted = this.isAcceptedFile(file)
            const isLtSize = file.size / 1024 / 1024 < this.maxSize

            if (!isAccepted) {
                this.$message.error('上传文件格式不符合要求')
            }
            if (!isLtSize) {
                this.$message.error(`文件大小不能超过 ${this.maxSize}MB!`)
            }
            return isAccepted && isLtSize
        }
    }
}
</script>

<style scoped>
.oss-uploader ::v-deep .el-upload {
    display: inline-block;
}

.oss-uploader--hidden {
    display: none;
}

.el-image__error,
.el-image__placeholder {
    background: #F5F7FA
}

.el-image__error,
.el-image__inner,
.el-image__placeholder {
    width: 100%;
    height: 100%
}

.el-image {
    position: relative;
    display: inline-block;
    overflow: hidden
}

.el-image__inner {
    vertical-align: top
}

.el-image__inner--center {
    position: relative;
    top: 50%;
    left: 50%;
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    display: block
}

.el-image__error {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    font-size: 14px;
    color: #C0C4CC;
    vertical-align: middle
}

.el-image__preview {
    cursor: pointer
}

.el-image-viewer__wrapper {
    position: relative;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0
}
</style>



// WEBPACK FOOTER //
// src/components/OssUpload.vue